import { useState } from 'react';
import type { ReviewGarment } from './review-service';

type GarmentImageProps = {
  garment: ReviewGarment;
  size?: 'xs' | 'sm' | 'md' | 'card' | 'tile';
};

function imageSize(size: NonNullable<GarmentImageProps['size']>): string {
  if (size === 'xs') return 'size-9 rounded-md';
  if (size === 'sm') return 'size-12 rounded-lg';
  if (size === 'tile') return 'aspect-square w-full rounded-lg';
  if (size === 'card') return 'h-48 w-full rounded-t-xl';
  return 'size-16 rounded-lg';
}

function GarmentImagePlaceholder({ size }: { size: NonNullable<GarmentImageProps['size']> }) {
  return (
    <span
      className={`flex ${imageSize(size)} shrink-0 items-center justify-center bg-secondary px-1 text-center text-xs text-tertiary`}
      aria-label="No garment image"
    >
      No image
    </span>
  );
}

function LoadableGarmentImage({
  garment,
  imageUrl,
  size,
}: {
  garment: ReviewGarment;
  imageUrl: string;
  size: NonNullable<GarmentImageProps['size']>;
}) {
  const [failed, setFailed] = useState(false);

  if (failed) return <GarmentImagePlaceholder size={size} />;

  return (
    <img
      src={imageUrl}
      alt={`${garment.title || 'Untitled garment'} garment`}
      className={`${imageSize(size)} shrink-0 bg-secondary object-contain`}
      onError={() => setFailed(true)}
    />
  );
}

export function GarmentImage({ garment, size = 'md' }: GarmentImageProps) {
  if (!garment.imageUrl) return <GarmentImagePlaceholder size={size} />;

  // The signed URL is always read from the latest queue payload. Keying the
  // loader by that value also clears a prior load error when a refresh supplies
  // a newly signed URL.
  return (
    <LoadableGarmentImage
      key={garment.imageUrl}
      garment={garment}
      imageUrl={garment.imageUrl}
      size={size}
    />
  );
}
